<template>
  <div class="fin-tops-flops">
    <h4 class="mt-12">Tops und Flops</h4>

    <fin-tabs :tab-items="regionTabs"
              :background-color-tabs="$finColors.background"
              :no-item-title-padding="true"
              :no-item-content-padding="true"
              :sub-tabs="false"
              :touchless="true"
              @tabChanged="tab => tabChanged(tab)">
      <template #actions>
        <fin-range-selector v-model="selectedRange"
                            :ranges="ranges"
                            :class="`${$vuetify.breakpoint.mdAndUp ? 'mb-6': 'mt-3 d-flex justify-end'}`"
                            @input="rangeChanged"
        />
      </template>
      <template #tabcontent>
        <div>
          <h5 class="mt-5 mb-2">Tops</h5>
          <fin-chart-list :items="tops" list-only :detail-options="detailOptions"/>
          <h5 class="mt-5 mb-2">Flops</h5>
          <fin-chart-list :items="flops" list-only :detail-options="detailOptions"/>
        </div>
      </template>
    </fin-tabs>

  </div>
</template>

<script>
import FinRangeSelector from "@/components/common/Chart2/RangeSelector"
import FinTabs from "@/components/common/Tabs"
import FinChartList from "@/components/common/Chart2/PerformanceCharts/ChartList"
import {DEFAULT_CHART_OPTIONS} from "@/components/common/Chart2/HighChartInit"
import {graphqlOperation} from "@/plugins/finAmplifyModules"
import FinChartBuilder from "@/components/common/Chart2/FinChartBuilder"
import MediumPerformanceChart from "@/components/common/Chart2/PerformanceCharts/MediumPerformanceChart"
import AmplitudePush from "@/mixins/AmplitudePush"
import {companyDetailsSubscription} from "@/js/finSubscriptions"

function adapt({searchInstruments}, breakpoint, $router, that) {
  return searchInstruments.items.map(item => {
    const data = [...item.timeseries, ...(item.realtime || [])]
    const seriesData = FinChartBuilder.mapTimeSeriesPoint(data, 'y', 'x')
    const result = FinChartBuilder.createCardSelectorChart(item.name, seriesData, 'medium',
      breakpoint, true, '€', 'auto', false, false, true)
    result.plotOptions = {
      series: {
        dataGrouping: that.range.dataGrouping
      }
    }

    if (result.tooltip) {
      result.tooltip.pointFormat = '{point.y}'
    } else {
      result.tooltip = {
        pointFormat: '{point.y}',
      }
    }

    result.subscriptions = [
      {
        subscription: graphqlOperation(companyDetailsSubscription, {id: item.id}),
        trigger({data}, {chart}) {
          const {price: y, timestamp, id} = data.onCreateRealTimePrice
          if (id !== item.id) {
            //check if this happens and how
            return
          }
          const x = (new Date(timestamp)).getTime()

          if (chart.series?.length) {
            chart.series[0].addPoint([x, y], true, false)
            const {min} = chart.xAxis[0].getExtremes()
            chart.xAxis[0].setExtremes(min, x, true, true, {trigger: 'addPoint'})
          }
        }
      }
    ]

    return {
      id: item.id,
      title: item.name,
      chart: result,
      onDetail: {
        click() {
          that.pushToAmplitude({
            'event': 'fincraft_finhub_topsflops_goto_instrument',
            'step': 'instrument_details',
            'placement': 'finhub_topsflops',
            'vue_breakpoint': breakpoint.name
          })
          if (that.region?.id !== 'ETF') {
            $router.push({name: 'companyDetail', params: {id: item.id}})
          } else {
            $router.push({name: 'etfDetail', params: {id: item.id}})
          }
        }
      },
    }
  })
}

export default {
  name: "fin-tops-flops",
  components: {
    FinChartList,
    FinTabs,
    FinRangeSelector,
  },
  mixins: [AmplitudePush],
  props: {},
  data() {
    return {
      height: 244,
      width: 210,
      selectedRange: 0,
      selectedRegion: 0,
      detailOptions: {
        type: () => MediumPerformanceChart,
        props: ({item, selected}) => ({
          title: item.titleDetail || item.title,
          chartOptions: item.chart
        })
      },
      regions: [
        {
          id: "DEU",
          text: "Aktien Deutschland",
          filter: {
            // filter same as in App Sync: filter: {and: {active: {eq: true}}, closeDate: {gte: "2022-05-18"}}
            and: {
              countryCode: {eq: "DE"},
              inDAX: {eq: "true"},
              active: {eq: true}
            }
          }
        }, {
          id: "EU",
          text: "Aktien Europa",
          filter: {
            and: {
              isEU: {eq: true},
              active: {eq: true}
            }
          }
        }, {
          id: "USA",
          text: "Aktien USA",
          filter: {
            and: {
              countryCode: {eq: "US"},
              active: {eq: true}
            }
          }
        }, {
          id: "ETF",
          text: "ETF",
          filter: {
            and: {
              active: {eq: true},
            }
          }
        }
      ],
      ranges: DEFAULT_CHART_OPTIONS.rangeSelector.buttons.filter(
        r => r.finTimeSeriesSuffix && r.finTimeSeriesSuffix !== "1D" &&
          r.finTimeSeriesSuffix && r.finTimeSeriesSuffix !== "6M"),
    }
  },
  computed: {
    region() {
      return this.regions[this.selectedRegion]
    },
    range() {
      return this.ranges[this.selectedRange]
    },
    baseQuery() {
      return `
          query topFlops(
            $filter: SearchableShareFilterInput
            $sortDirection: SearchableSortDirection
            $limit: Int)
          {
            searchInstruments: searchShares(
                filter: $filter,
                sort: {direction: $sortDirection, field: closeChange${this.range.finTimeSeriesSuffix} }
                limit: $limit
              ) {
                items {
                  id
                  name
                  change: closeChange${this.range.finTimeSeriesSuffix}
                  timeseries: timeSeries${this.range.finTimeSeriesSuffix} {
                        x: timestamp
                        y: close
                  },
                  realtime: timeSeriesRT {
                    x:timestamp
                    y:price
                  }
                }
              }
            }
        `
    },
    etfQuery() {
      return `
          query topFlops(
            $filter: SearchableEtfFilterInput
            $sortDirection: SearchableSortDirection
            $limit: Int)
          {
            searchInstruments: searchEtfs(
                filter: $filter,
                sort: {direction: $sortDirection, field: closeChange${this.range.finTimeSeriesSuffix} }
                limit: $limit
              ) {
                items {
                  id
                  name
                  change: closeChange${this.range.finTimeSeriesSuffix}
                  timeseries: timeSeries${this.range.finTimeSeriesSuffix} {
                        x: timestamp
                        y: close
                  },
                  realtime: timeSeriesRT {
                    x:timestamp
                    y:price
                  }
                }
              }
            }
        `
    },
    tops() {
      return this.charts('desc')
    },
    flops() {
      return this.charts('asc')
    },
    regionTabs() {
      return this.regions.map((region) => ({
        name: region.text
      }))
    }
  },
  methods: {
    calculateRangeMsDiff(date) {
      const datetime = date.getTime()
      const msOneDay = 1000 * 60 * 60 * 24
      let dateTimeAgo = undefined
      // FYI: setUTCDate/Month with non-existing dates in correct format returns the next day.
      // For example: 30.2.YYYY returns 1.3.YYYY
      if (this.range.count === 1 && this.range.type === 'week') {
        return 7 * msOneDay
      } else if (this.range.count === 1 && this.range.type === 'month') {
        dateTimeAgo = new Date().setUTCMonth(date.getUTCMonth() > 0 ? date.getUTCMonth() - 1 : 11)
      } else if (this.range.count === 3 && this.range.type === 'month') {
        dateTimeAgo = new Date().setUTCMonth(date.getUTCMonth() > 2 ? date.getUTCMonth() - 3 : 10 + date.getUTCMonth())
      } else if (this.range.count === 1 && this.range.type === 'year') {
        dateTimeAgo = new Date().setUTCFullYear(date.getUTCFullYear() - 1)
      } else {
        console.error("calculateRangeMsDiff not calculated for range ", this.range)
        return 0
      }
      return datetime - dateTimeAgo
    },
    adaptedFilter() {
      // adapt filter to filter too old instruments out
      let filter = {...this.region.filter}
      const now = new Date()
      let range = this.range.count === 1 && this.calculateRangeMsDiff(now)
      const date = new Date(now - range)
      const dateStr = date.toISOString().split('T')[0] // format: 2022-05-12

      if (filter.and && filter.and.closeDate === undefined) {
        filter.and.closeDate = {gte: '' + dateStr}
      }

      return filter
    },
    charts(sort) {
      const that = this
      return {
        key: 'topsflops_' + this.region.id + '_' + this.range.text + '_' + sort,
        load: graphqlOperation(this.region.id === 'ETF' ? this.etfQuery : this.baseQuery,
          {limit: 8, sortDirection: sort, filter: that.adaptedFilter()}),
        adapt: (data) => adapt(data, this.$vuetify.breakpoint, this.$router, that)
      }
    },
    rangeChanged() {
      //trigger resize to update dynamic scroller
      //TODO maybe improve
      window.dispatchEvent(new Event('resize'))
    },
    tabChanged(tab) {
      this.selectedRegion = tab
      this.pushToAmplitude({
        'event': 'fincraft_finhub_topsflops_region_select',
        'step': 'topsflops_region_select',
        'step_choice': this.regionTabs[tab].name,
        'placement': 'finhub_topsflops',
        'vue_breakpoint': this.$vuetify.breakpoint.name
      })
    }
  }
}
</script>

<style lang="scss" scoped>

.fin-top-flop__title {
  font-size: 1.2em !important;
  line-height: 1.25;
  height: 58px;
  overflow: hidden;
}

.fin-tops-flops h5 {
  font-size: 1.3em;
  font-weight: bold;
  line-height: 1.1;
}

</style>
